import React, { useContext, useLayoutEffect, useCallback, useState, Fragment } from 'react';
import { CarrierDriverFilter } from 'components/ShipmentFilters';
import { pushWithQuery as push } from 'utils/routing';
import { preserveScroll } from 'utils/router';
import config from 'config';
import style from '../style.module.scss';
import { Table, TableRow, TableHeader, TableBody, TableCell } from 'components/Table';
import CarriersContext from '../context';
import { Loading } from 'components/Loading';
import { Tag, Button } from '@ontruck/orc';
import cx from 'classnames';
import tel from 'utils/phone';
import { useInView } from 'react-intersection-observer';
import moment from 'moment';
import CarrierHomeFilters from './CarrierHomeFilters';
import { CARRIER_STATES } from 'components/CarrierDetails/Constants';
import LogCarrierNotes from 'components/LogCarrierNotes';
import useCarriersLite from 'utils/hooks/useCarriersLite';
import { injectIntl } from 'react-intl';
import { FormattedMessage } from 'react-intl';
import { NewCarrierButton } from './NewCarrierButton';

const CarrierHome = ({ intl, query, onFilterCarrier }) => {
  const translation = intl.messages.components.CarrierHome;
  const t = translation.table;
  const { carrierData, loading, fetchNextCarriers, carriers, order, loadingMore, clearFilters } =
    useContext(CarriersContext);
  const carriersLiteConfig = useCarriersLite();

  const [prevY, setPrevY] = useState(0);

  const pushToCarrierDetail = useCallback(
    (carrierId) => {
      if (!carrierId) return;

      return (
        carrierId &&
        push({ pathname: `${config.BASE_ROUTE}carrier/${carrierId}`, query, ...preserveScroll })
      );
    },
    [query]
  );

  const openCarrier = useCallback(
    (change) => {
      if (!change) return;
      const { value } = change || {};
      if (!value) return;

      const { carrier_id } = value || {};
      const carrierId = carrier_id ? carrier_id : value.id;

      return pushToCarrierDetail(carrierId);
    },
    [pushToCarrierDetail]
  );

  const [ref, inView, entry] = useInView({ threshold: 1 });

  useLayoutEffect(() => {
    const y = entry && entry.boundingClientRect.y;
    if (inView && !!carrierData.next && !loading && !loadingMore && prevY > y) {
      fetchNextCarriers(carrierData.next);
    }
    setPrevY(y);
  }, [inView, carrierData.next, fetchNextCarriers, loading, loadingMore, entry, prevY]);

  const SortableHeader = ({ label, order, fieldName, left = false }) => {
    return (
      <TableCell className={cx({ [style.cellLeft]: left })} key={`header-${label}`}>
        <span className={cx({ [style.cellBold]: order.field === fieldName })}>{label}</span>
        <span
          className={cx(style.headerIcon, {
            [style.headerIconAsc]: order.field !== fieldName || order.direction === '+',
          })}
        ></span>
      </TableCell>
    );
  };

  const renderStatus = (carrier) => {
    if (carrier.status !== CARRIER_STATES.banned && carrier.status === CARRIER_STATES.active)
      return;
    if (carrier.status === CARRIER_STATES.banned) {
      return <Tag text={t.banned} skin="modernRed" weight="bold" size="M" />;
    }
    if (carrier.status === CARRIER_STATES.not_active) {
      return <Tag text={t.not_activated} skin="modernGrey" weight="bold" size="M" />;
    }
  };

  const renderPhoneButton = (phone) => {
    return (
      <Button
        icon="phone"
        iconPosition="left"
        ghost
        skin="primary"
        onClick={(e) => {
          e.stopPropagation();
          window.open(tel(phone), '_self');
        }}
      />
    );
  };

  const renderScoring = (score) => {
    if (!score) return '';
    return `${parseFloat(score) * 100}%`;
  };

  const renderCarriersRows = ({ isCategoryDisabled, isScoreDisabled, isCarrierTypeDisabled }) => {
    if (!carriers) return;
    return carriers.map((carrier, index) => {
      const showRef = carriers.length === index + 1;
      const carrierType = carrier.carrier_type?.replace(/ /g, '_').toLowerCase();
      return (
        <Fragment key={carrier.id}>
          <TableRow
            className={cx(style.tableRow, {
              [style.notActive]: !carrier.status === CARRIER_STATES.not_active,
            })}
            onRowClick={() => pushToCarrierDetail(carrier.id)}
            dataTestId="carriersTableRow"
          >
            <TableCell className={style.cellLeft}>{carrier.name}</TableCell>
            {!isCategoryDisabled && (
              <TableCell className={style.cellBold}>{carrier.category}</TableCell>
            )}
            {!isCarrierTypeDisabled && <TableCell>{t.type[carrierType]}</TableCell>}
            {!isScoreDisabled && (
              <TableCell className={style.cellBold}>{renderScoring(carrier.scoring)}</TableCell>
            )}
            <TableCell>{`${carrier.active_vehicles}/${carrier.vehicles.length}`}</TableCell>
            <TableCell>{`${carrier.active_drivers}/${carrier.drivers.length}`}</TableCell>
            <TableCell className={style.buttonCell}>
              <Button
                nude
                skin="primary"
                onClick={(e) => {
                  e.stopPropagation();
                  onFilterCarrier(carrier);
                }}
              >
                {carrier.number_of_journeys.toString()}
              </Button>
            </TableCell>
            <TableCell>
              {carrier.last_journey_datetime &&
                moment(carrier.last_journey_datetime).format('DD MMM YYYY')}
            </TableCell>
            <TableCell>
              <LogCarrierNotes log={carrier.status_log} id={carrier.id} />
            </TableCell>
            <TableCell>{renderStatus(carrier)}</TableCell>
            <TableCell className={style.hoverHidden}>{renderPhoneButton(carrier.phone)}</TableCell>
          </TableRow>
          {showRef && <tr key={`load-more-row-${carriers.length}`} ref={ref}></tr>}
        </Fragment>
      );
    });
  };

  const renderCarriersTable = ({ isCategoryDisabled, isScoreDisabled, isCarrierTypeDisabled }) => {
    if (loading || !carriers)
      return (
        <div className={style.loading}>
          <Loading />
        </div>
      );
    return (
      <>
        <div className={style.carrierCounter}>
          <span>
            <FormattedMessage
              id="CarrierHome.table.carriers_shown"
              defaultMessage={t.carriers_shown}
              values={{ number: carriers.length, total: carrierData.count }}
            />
          </span>
          <span className={style.clearSeparator}>{` - `}</span>
          <span className={style.clearButton}>
            <Button text={t.clear_filters} nude skin="primary" onClick={() => clearFilters()} />
          </span>
        </div>
        <Table>
          <TableHeader className={style.tableHeader}>
            <TableRow className={style.tableHeaderRow}>
              <SortableHeader label={t.name} order={order} fieldName="name" left={true} />
              {!isCategoryDisabled && <TableCell>{t.category}</TableCell>}
              {!isCarrierTypeDisabled && <TableCell>{t.carrier_type}</TableCell>}
              {!isScoreDisabled && (
                <SortableHeader label={t.scoring} order={order} fieldName="scoring" />
              )}
              <TableCell>{t.vehicles}</TableCell>
              <TableCell>{t.drivers}</TableCell>
              <SortableHeader label={t.jobs} order={order} fieldName="number_of_journeys" />
              <SortableHeader label={t.last_job} order={order} fieldName="last_journey_datetime" />
            </TableRow>
          </TableHeader>
          <TableBody>{renderCarriersRows(carriersLiteConfig)}</TableBody>
        </Table>
      </>
    );
  };

  return (
    <div className={style.container}>
      <div className={style.actionsBar}>
        <CarrierHomeFilters t={translation.filters} />
        <NewCarrierButton />
        <div className={style.searchContainer}>
          <CarrierDriverFilter onChange={openCarrier} />
        </div>
      </div>
      <div className={style.tableContainer} data-testid="carriersTable">
        {renderCarriersTable(carriersLiteConfig)}
        {loadingMore && (
          <div className={style.loading}>
            <Loading />
          </div>
        )}
      </div>
    </div>
  );
};

export default injectIntl(CarrierHome);
